import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchAddonOptions, fetchClientServices, getAddonPrice } from "./addonAPI";

const initialState = {
  currentService: null,
  availableAddons: {},
  activeAddons: [],
  clientServices: [],
  selectedService: null,
  selectedAddon: null,
  selectedBillingCycle: null,
  addonPrice: 0,
  status: "idle",
  clientServicesStatus: "idle",
  error: "",
};

export const getAddonOptions = createAsyncThunk(
  "addon/getAddonOptions",
  async (serviceid, { rejectWithValue }) => {
    try {
      const response = await fetchAddonOptions(serviceid);
      
      if (response.status === "error") {
        return rejectWithValue(response.message);
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getClientServices = createAsyncThunk(
  "addon/getClientServices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchClientServices();
      
      if (response.status === "error") {
        return rejectWithValue(response.message);
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const addonSlice = createSlice({
  name: "addon",
  initialState,
  reducers: {
    setSelectedService: (state, action) => {
      state.selectedService = action.payload;
    },
    setSelectedAddon: (state, action) => {
      state.selectedAddon = action.payload;
      state.selectedBillingCycle = null;
      state.addonPrice = 0;
    },
    setSelectedBillingCycle: (state, action) => {
      state.selectedBillingCycle = action.payload;
      
      // Calculate addon price when billing cycle is selected
      if (state.selectedAddon && action.payload) {
        const addonPrice = getAddonPrice(state.selectedAddon, action.payload);
        state.addonPrice = addonPrice;
      }
    },
    resetAddonSelection: (state) => {
      state.selectedService = null;
      state.selectedAddon = null;
      state.selectedBillingCycle = null;
      state.addonPrice = 0;
    },
    clearAddonData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAddonOptions.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getAddonOptions.fulfilled, (state, action) => {
        state.status = "idle";
        state.currentService = action.payload.current_service;
        state.availableAddons = action.payload.available_addons || {};
        state.activeAddons = action.payload.active_addons || [];
        
        // Reset selections
        state.selectedService = null;
        state.selectedAddon = null;
        state.selectedBillingCycle = null;
        state.addonPrice = 0;
      })
      .addCase(getAddonOptions.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload || "Failed to fetch addon options";
        state.currentService = null;
        state.availableAddons = {};
      })
      .addCase(getClientServices.pending, (state) => {
        state.clientServicesStatus = "loading";
        state.error = "";
      })
      .addCase(getClientServices.fulfilled, (state, action) => {
        state.clientServicesStatus = "idle";
        state.clientServices = action.payload.user_services || [];
        state.selectedService = null;
      })
      .addCase(getClientServices.rejected, (state, action) => {
        state.clientServicesStatus = "error";
        state.error = action.payload || "Failed to fetch client services";
        state.clientServices = [];
      });
  },
});

export const {
  setSelectedService,
  setSelectedAddon,
  setSelectedBillingCycle,
  resetAddonSelection,
  clearAddonData,
} = addonSlice.actions;

export default addonSlice.reducer;
